import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Map, RefreshCw, Sparkles, Bot, CheckCircle2, ExternalLink, Target } from "lucide-react";
import MissionLauncherDialog from "@/components/MissionLauncherDialog";
import CoverageObjectivesDialog from "@/components/CoverageObjectivesDialog";
import { Link } from "react-router-dom";

const LEVEL_CLASS = {
  good: "bg-emerald-500/15 hover:bg-emerald-500/25 border-emerald-500/40 text-emerald-700 dark:text-emerald-300",
  partial: "bg-amber-500/15 hover:bg-amber-500/25 border-amber-500/40 text-amber-700 dark:text-amber-300",
  gap: "bg-rose-500/10 hover:bg-rose-500/20 border-rose-500/30 text-rose-700 dark:text-rose-300",
};

const LEVEL_DOT = {
  good: "bg-emerald-500",
  partial: "bg-amber-500",
  gap: "bg-rose-500",
};

export default function CoverageHeatMap() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillCell, setDrillCell] = useState(null); // {principal, country}
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [missionOpen, setMissionOpen] = useState(false);
  const [missionPreset, setMissionPreset] = useState(null);
  const [objectivesOpen, setObjectivesOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/coverage/heat-map");
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openDrill = async (principal, country) => {
    setDrillCell({ principal, country });
    setDrillLoading(true);
    setDrillData(null);
    try {
      const { data: d } = await api.get("/coverage/cell", {
        params: { principal_id: principal.id, country_code: country.code },
      });
      setDrillData(d);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDrillLoading(false);
    }
  };

  const launchMissionFromCell = () => {
    if (!drillCell) return;
    setMissionPreset({
      principalId: drillCell.principal.id,
      countries: [drillCell.country.code],
    });
    setDrillCell(null);
    setMissionOpen(true);
  };

  const matrix = data?.matrix || {};
  const principals = data?.principals || [];
  const countries = data?.countries || [];

  // Show countries that have data OR have a target override defined
  const visibleCountries = useMemo(() => {
    if (!data) return [];
    const overrides = data.objectives?.overrides || {};
    return countries.filter((c) =>
      principals.some(
        (p) =>
          (matrix[p.id]?.[c.code]?.total || 0) > 0 ||
          overrides[`${p.id}__${c.code}`]
      )
    );
  }, [data, countries, principals, matrix]);

  return (
    <>
      <Card data-testid="coverage-heat-map">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Map className="h-4 w-4 text-emerald-600" />
              <h3 className="font-semibold text-sm">{t("coverage.heat_map_title")}</h3>
              <Badge variant="outline" className="text-[10px]">
                {t("coverage.target_per_cell", { n: data?.objectives?.default_target ?? 10 })}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-500" /> 100%+
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-amber-500" /> 50-99%
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-rose-500" /> &lt; 50%
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setObjectivesOpen(true)}
                className="h-7 text-[11px]"
                data-testid="open-objectives-btn"
              >
                <Target className="h-3 w-3 mr-1.5" /> {t("coverage.objectives_btn")}
              </Button>
              <Button variant="ghost" size="sm" onClick={load} className="h-6 px-2">
                <RefreshCw className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">{t("coverage.loading")}</div>
          ) : visibleCountries.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground space-y-3">
              <p>{t("coverage.no_data")}</p>
              <p className="text-[11px]">
                {t("coverage.no_data_hint")}
              </p>
              <Button variant="outline" size="sm" onClick={() => setObjectivesOpen(true)}>
                <Target className="h-3.5 w-3.5 mr-2" /> {t("coverage.set_objectives")}
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="heat-map-table">
                <thead>
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground sticky left-0 bg-card z-10">
                      {t("coverage.mandant")}
                    </th>
                    {visibleCountries.map((c) => (
                      <th key={c.code} className="px-1 py-2 font-medium text-center min-w-[64px]">
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="text-base leading-none">{c.flag}</span>
                          <span className="text-[10px] text-muted-foreground">{c.code}</span>
                        </div>
                      </th>
                    ))}
                    <th className="px-2 py-2 font-medium text-center text-muted-foreground">
                      {t("coverage.total")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {principals.map((p) => {
                    const tot = data.totals_per_principal[p.id] || { total: 0, verified_emails: 0 };
                    return (
                      <tr key={p.id} className="border-t border-border/50">
                        <td className="px-3 py-2 font-semibold text-sm sticky left-0 bg-card z-10">{p.name}</td>
                        {visibleCountries.map((c) => {
                          const cell = matrix[p.id]?.[c.code] || {
                            total: 0, target: data?.objectives?.default_target ?? 10,
                            verified_emails: 0, level: "gap", progress_pct: 0,
                          };
                          const target = cell.target ?? data?.objectives?.default_target ?? 10;
                          const pct = Math.min(100, cell.progress_pct ?? 0);
                          return (
                            <td key={c.code} className="px-1 py-1">
                              <button
                                onClick={() => openDrill(p, c)}
                                className={`w-full rounded-md border p-1.5 transition cursor-pointer text-center ${LEVEL_CLASS[cell.level]}`}
                                data-testid={`cell-${p.id}-${c.code}`}
                                title={`${p.name} × ${c.name}: ${cell.total} / ${target} (${cell.progress_pct ?? 0}%) — ${cell.verified_emails} vérifiés`}
                              >
                                <div className="font-bold leading-tight">
                                  {cell.total}<span className="opacity-50 font-normal">/{target}</span>
                                </div>
                                <div className="h-0.5 mt-1 rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
                                  <div
                                    className={`h-full ${
                                      cell.level === "good"
                                        ? "bg-emerald-500"
                                        : cell.level === "partial"
                                        ? "bg-amber-500"
                                        : "bg-rose-500"
                                    }`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <div className="text-[9px] opacity-75 mt-0.5">{cell.verified_emails}✓</div>
                              </button>
                            </td>
                          );
                        })}
                        <td className="px-2 py-2 text-center">
                          <div className="font-bold">{tot.total}</div>
                          <div className="text-[9px] text-muted-foreground">{tot.verified_emails}✓</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[10px] text-muted-foreground mt-3">
            {t("coverage.cell_hint")}
          </p>
        </CardContent>
      </Card>

      {/* Drill-down dialog */}
      <Dialog open={!!drillCell} onOpenChange={(v) => !v && setDrillCell(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {drillCell?.principal?.name} × {drillCell?.country?.flag} {drillCell?.country?.name}
            </DialogTitle>
            <DialogDescription>
              {t("coverage.drill_description")}
            </DialogDescription>
          </DialogHeader>

          {drillLoading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">{t("coverage.loading")}</div>
          ) : (
            <div className="space-y-5">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: t("coverage.contacts"), value: drillData?.stats?.total || 0 },
                  { label: t("coverage.verified_emails"), value: drillData?.stats?.verified_emails || 0 },
                  { label: t("coverage.decision_makers"), value: drillData?.stats?.decision_makers || 0 },
                  { label: "LinkedIn", value: drillData?.stats?.with_linkedin || 0 },
                ].map((s) => (
                  <Card key={s.label}><CardContent className="p-3 text-center">
                    <div className="text-2xl font-bold">{s.value}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 uppercase tracking-wider">{s.label}</div>
                  </CardContent></Card>
                ))}
              </div>

              {/* Gap call-to-action - shown when below 50% of target */}
              {(() => {
                const cellTarget = matrix[drillCell?.principal?.id]?.[drillCell?.country?.code]?.target
                  ?? data?.objectives?.default_target ?? 10;
                const cur = drillData?.stats?.total || 0;
                if (cur >= Math.ceil(cellTarget * 0.5)) return null;
                return (
                  <Card className="border-rose-300 bg-rose-50/50 dark:bg-rose-950/20">
                    <CardContent className="p-4 flex items-start gap-3">
                      <div className="h-8 w-8 rounded-full bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center flex-shrink-0">
                        <Bot className="h-4 w-4 text-rose-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">
                          Couverture insuffisante ({cur} / {cellTarget})
                        </p>
                        <p className="text-xs text-muted-foreground mb-3">
                          Cette combinaison Mandant × Pays est sous votre objectif. Lancez une Mission Auto
                          pour la combler en ~5 minutes.
                        </p>
                        <Button onClick={launchMissionFromCell} size="sm" data-testid="cell-launch-mission">
                          <Sparkles className="h-3.5 w-3.5 mr-2" />
                          🤖 Lancer une Mission Auto sur {drillCell?.country?.code}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Sources breakdown */}
              {drillData?.stats?.by_source && Object.keys(drillData.stats.by_source).length > 0 && (
                <div>
                  <h4 className="text-xs uppercase font-semibold text-muted-foreground mb-2">
                    Sources des contacts
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(drillData.stats.by_source).map(([src, n]) => (
                      <Badge key={src} variant="outline" className="text-[10px]">
                        {src} · {n}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Top contacts */}
              {(drillData?.contacts?.length || 0) > 0 && (
                <div>
                  <h4 className="text-xs uppercase font-semibold text-muted-foreground mb-2">
                    Top contacts ({drillData.contacts.length})
                  </h4>
                  <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
                    {drillData.contacts.map((c) => (
                      <div key={c.id} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded border border-border/50 hover:bg-muted/40">
                        {c.email_verified && <CheckCircle2 className="h-3 w-3 text-emerald-600 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">
                            {c.first_name} {c.last_name}
                            {c.is_decision_maker && <span className="ml-1 text-amber-600">★</span>}
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate">{c.position || "—"}</div>
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate max-w-[180px]">
                          {c.email || "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Companies */}
              {(drillData?.companies?.length || 0) > 0 && (
                <div>
                  <h4 className="text-xs uppercase font-semibold text-muted-foreground mb-2">
                    Entreprises dans cette zone ({drillData.companies.length})
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {drillData.companies.map((co) => (
                      <Link key={co.id} to={`/companies/${co.id}`}>
                        <Badge variant="outline" className="text-[10px] hover:bg-emerald-50 cursor-pointer">
                          {co.name}
                          <ExternalLink className="h-2.5 w-2.5 ml-1" />
                        </Badge>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Action footer */}
              <div className="pt-2 border-t flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setDrillCell(null)}>Fermer</Button>
                <Button onClick={launchMissionFromCell} size="sm">
                  <Sparkles className="h-3.5 w-3.5 mr-2" /> Lancer Mission ciblée
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <MissionLauncherDialog
        open={missionOpen}
        onOpenChange={setMissionOpen}
        defaultPrincipalId={missionPreset?.principalId}
        defaultCountries={missionPreset?.countries}
        onLaunched={() => { setMissionOpen(false); load(); }}
      />

      <CoverageObjectivesDialog
        open={objectivesOpen}
        onOpenChange={setObjectivesOpen}
        principals={principals}
        countries={countries}
        onSaved={load}
      />
    </>
  );
}
